import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Droplets, Power, Gauge, FilterIcon, AlertTriangle,
  Activity, TrendingUp, Loader2, CheckCircle2, XCircle,
} from 'lucide-react';
import { adminApi } from '../../utils/admin-api';
import { API_BASE } from '../../utils/api-base';

// ==================== TYPES ====================

interface TagBinding {
  plcId: string;
  tagName: string;
  plcName?: string;
}

interface EquipmentItem {
  id: string;
  label: string;
  binding: TagBinding | null;
}

interface PlantKpiConfig {
  targetProduction: number;
  targetUnit: string;
  bindings: Record<string, TagBinding>;
  pumps: EquipmentItem[];
  filters: EquipmentItem[];
}

const DEFAULT_CONFIG: PlantKpiConfig = {
  targetProduction: 0,
  targetUnit: 'L',
  bindings: {},
  pumps: [],
  filters: [],
};

const bindingKey = (b: TagBinding | null | undefined) => (b ? `${b.plcId}::${b.tagName}` : null);

const fmtNum = (value: number | null, digits = 1) =>
  value === null || !Number.isFinite(value) ? '—' : value.toLocaleString('en-US', { maximumFractionDigits: digits });

// ==================== COMPONENT ====================
// Read-only: which PLC tags feed each KPI is configured by an admin in the Super
// Admin portal (Plant KPI Config tab), not from here — clients just see the result.

export function PlantKPIDashboard({
  clientId, token, pollingIntervalMs,
}: {
  clientId: string;
  token: string;
  pollingIntervalMs?: number;
}) {
  const [config, setConfig] = useState<PlantKpiConfig>(DEFAULT_CONFIG);
  const [liveValues, setLiveValues] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---- Load config ----
  const loadConfig = useCallback(async () => {
    try {
      const res = await adminApi.getPlantKpiConfig(token, clientId);
      const cfg: PlantKpiConfig = { ...DEFAULT_CONFIG, ...(res?.config || {}) };
      setConfig(cfg);
    } catch (err) {
      console.error('Failed to load plant KPI config:', err);
    }
  }, [token, clientId]);

  useEffect(() => {
    if (!clientId || !token) return;
    setLoading(true);
    loadConfig().finally(() => setLoading(false));
  }, [clientId, token, loadConfig]);

  // ---- Poll live values ----
  useEffect(() => {
    if (!clientId || !token) return;

    const fetchValues = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/latest-values`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json();
        if (!response.ok) return;
        const next: Record<string, number> = {};
        for (const tag of payload?.tags || []) {
          if (tag.value === null || tag.value === undefined) continue;
          next[`${tag.plcId}::${tag.name}`] = Number(tag.value);
        }
        setLiveValues(next);
        setLastUpdatedAt(Date.now());
      } catch (err) {
        console.error('Failed to fetch plant KPI live values:', err);
      }
    };

    fetchValues();
    const intervalMs = Math.max(3000, Number(pollingIntervalMs) || 5000);
    pollRef.current = setInterval(fetchValues, intervalMs);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [clientId, token, pollingIntervalMs]);

  const getValue = useCallback((binding: TagBinding | null | undefined): number | null => {
    const key = bindingKey(binding);
    if (!key) return null;
    const value = liveValues[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }, [liveValues]);

  // ---- Derived KPI values ----
  const derived = useMemo(() => {
    const plantRunning = getValue(config.bindings.plantRunningStatus);
    const productionToday = getValue(config.bindings.productionToday);
    const totalFiltered = getValue(config.bindings.totalWaterFiltered);
    const flowRate = getValue(config.bindings.flowRate);
    const runningHours = getValue(config.bindings.filterRunningHours);
    const backwash = getValue(config.bindings.backwashFlow);
    const treated = getValue(config.bindings.treatedFlow);

    const target = config.targetProduction;
    const productionPct = target > 0 && productionToday !== null
      ? Math.max(0, (productionToday / target) * 100)
      : null;

    const backwashPct = backwash !== null && treated !== null && treated > 0
      ? (backwash / treated) * 100
      : null;

    const pumpsRunning = config.pumps.filter(p => {
      const v = getValue(p.binding);
      return v !== null && v > 0;
    }).length;
    const filtersRunning = config.filters.filter(f => {
      const v = getValue(f.binding);
      return v !== null && v > 0;
    }).length;

    const totalEquipment = config.pumps.length + config.filters.length;
    const runningEquipment = pumpsRunning + filtersRunning;
    const availabilityPct = totalEquipment > 0 ? (runningEquipment / totalEquipment) * 100 : null;

    const stoppedAlarms = (config.pumps.length + config.filters.length) - runningEquipment;

    return {
      plantRunning, productionToday, totalFiltered, flowRate, runningHours,
      backwashPct, productionPct, pumpsRunning, filtersRunning, totalEquipment,
      runningEquipment, availabilityPct, activeAlarms: Math.max(0, stoppedAlarms),
    };
  }, [config, getValue]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading plant KPIs…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-cyan-500/15 flex items-center justify-center">
            <Droplets className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold">Plant KPI Dashboard</h2>
            <p className="text-xs text-muted-foreground">
              {lastUpdatedAt ? `Updated ${new Date(lastUpdatedAt).toLocaleTimeString()}` : 'Waiting for data…'}
            </p>
          </div>
          <Badge variant="outline" className="ml-1 border-emerald-500/40 text-emerald-500 text-[10px]">LIVE</Badge>
        </div>
      </div>

      {/* Hero row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className={derived.plantRunning === null ? '' : derived.plantRunning > 0 ? 'border-emerald-500/40' : 'border-rose-500/40'}>
            <CardContent className="pt-5 flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                derived.plantRunning === null ? 'bg-slate-500/15' : derived.plantRunning > 0 ? 'bg-emerald-500/15' : 'bg-rose-500/15'
              }`}>
                <Power className={`w-5 h-5 ${derived.plantRunning === null ? 'text-slate-400' : derived.plantRunning > 0 ? 'text-emerald-500' : 'text-rose-500'}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Plant Running Status</p>
                <p className={`text-lg font-bold ${derived.plantRunning === null ? 'text-muted-foreground' : derived.plantRunning > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {derived.plantRunning === null ? 'Not bound' : derived.plantRunning > 0 ? 'Running' : 'Stopped'}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-muted-foreground">Production Today vs Target</p>
                <TrendingUp className="w-4 h-4 text-blue-400" />
              </div>
              <p className="text-lg font-bold">
                {fmtNum(derived.productionToday)} <span className="text-xs text-muted-foreground font-normal">{config.targetUnit}</span>
                {config.targetProduction > 0 && (
                  <span className="text-xs text-muted-foreground font-normal"> / {fmtNum(config.targetProduction)}</span>
                )}
              </p>
              {derived.productionPct !== null ? (
                <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full ${derived.productionPct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(100, derived.productionPct)}%` }}
                  />
                </div>
              ) : (
                <p className="text-[10px] text-muted-foreground mt-1">Ask your administrator to bind a production target/tag to see progress</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className={derived.activeAlarms > 0 ? 'border-amber-500/40' : ''}>
            <CardContent className="pt-5 flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${derived.activeAlarms > 0 ? 'bg-amber-500/15' : 'bg-emerald-500/15'}`}>
                <AlertTriangle className={`w-5 h-5 ${derived.activeAlarms > 0 ? 'text-amber-500' : 'text-emerald-500'}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Active Alarms</p>
                <p className={`text-lg font-bold ${derived.activeAlarms > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {derived.totalEquipment > 0 ? derived.activeAlarms : '—'}
                </p>
                <p className="text-[10px] text-muted-foreground">Stopped pumps/filters</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {[
          { label: 'Flow Rate', value: derived.flowRate, unit: 'm³/h', icon: Gauge, color: 'text-cyan-500' },
          { label: 'Total Water Filtered', value: derived.totalFiltered, unit: config.targetUnit, icon: Droplets, color: 'text-blue-500' },
          { label: 'Equipment Availability', value: derived.availabilityPct, unit: '%', icon: Activity, color: 'text-emerald-500' },
          { label: 'Filter Running Hours', value: derived.runningHours, unit: 'hrs', icon: FilterIcon, color: 'text-indigo-500' },
          { label: 'Treated Backwash Water', value: derived.backwashPct, unit: '%', icon: Droplets, color: 'text-purple-500' },
        ].map(kpi => (
          <Card key={kpi.label}>
            <CardContent className="pt-5">
              <kpi.icon className={`w-4 h-4 mb-2 ${kpi.color}`} />
              <p className="text-xl font-bold">{fmtNum(kpi.value)}<span className="text-xs text-muted-foreground font-normal ml-1">{kpi.value !== null ? kpi.unit : ''}</span></p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pumps + Filters status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(['pumps', 'filters'] as const).map(listKey => (
          <Card key={listKey}>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                {listKey === 'pumps' ? <Power className="w-4 h-4" /> : <FilterIcon className="w-4 h-4" />}
                {listKey === 'pumps' ? 'Pump Status' : 'Filter Status'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {config[listKey].length === 0 ? (
                <p className="text-xs text-muted-foreground italic">None configured yet.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {config[listKey].map(item => {
                    const v = getValue(item.binding);
                    const running = v !== null && v > 0;
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                          v === null ? 'border-border/50 bg-muted/30' : running ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'
                        }`}
                      >
                        {v === null ? (
                          <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0" />
                        ) : running ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                        )}
                        <span className="text-xs font-medium truncate">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
